/**
 * aiProposalAdmin — constantes et fonctions pures de l'écran « Propositions
 * d'agents » (UPT-2016, lot 2) : liste `/portal/{ws}/core/ai-proposals` (+
 * adminportal, sans année).
 *
 * Écran écrit à la main (pas de SchemaDrivenForm) : une proposition n'a ni fiche
 * à modifier ni dictionnaire `core.field_schema`, seulement une charge utile à
 * relire puis une décision. Les accesseurs de clés i18n sont ICI, au niveau
 * module : la règle i18n refuse une clé composée dans le JSX.
 */

import type {
  AiProposalDto,
  AiProposalOperation,
  AiProposalSource,
  AiProposalStatus,
} from '@/uptk/core/api/AiProposalApi';
import type { ModuleRouteNode } from '@/types/route_management';
import type { BadgeTone } from '@/uptk/core/components/ui/statusBadgeUtils';
import { parseRoutePathDefinition } from '@/utils/route_management';

/** Domaine / entité de navigation (pseudo-entité `core/aiproposal`, slug `ai-proposals`). */
export const AI_PROPOSAL_DOMAIN = 'core';
export const AI_PROPOSAL_ENTITY = 'aiproposal';
/** Entité de permission du serveur (`AIPROPOSAL.LIST|READ|UPDATE`) : UPDATE = approuver ou refuser. */
export const AI_PROPOSAL_PERMISSION_ENTITY = 'AIPROPOSAL';

const LIST_SEGMENT = 'ai-proposals';

/** Statut proposé à l'ouverture de la page : la file à traiter. */
export const DEFAULT_STATUS_FILTER: AiProposalStatusFilter = 'PENDING';

/** Valeur « tous les statuts » du filtre. */
export const ALL_STATUSES = 'ALL';

export type AiProposalStatusFilter = AiProposalStatus | typeof ALL_STATUSES;

/** Ordre des options du filtre : la file à traiter d'abord, puis le résultat des décisions. */
export const STATUS_FILTER_VALUES: AiProposalStatusFilter[] = [
  'PENDING',
  'EXECUTED',
  'REJECTED',
  'FAILED',
  'APPROVED',
  ALL_STATUSES,
];

/** Nombre maximal de paires affichées dans le résumé d'une charge utile. */
export const SUMMARY_MAX_ENTRIES = 3;
/** Longueur maximale du résumé (grille, carte mobile). */
export const SUMMARY_MAX_LENGTH = 120;

export type AiProposalAdminPaths = {
  /** `/portal/{ws}/core/ai-proposals` */
  list: string;
};

/** Chemin de la liste, dans la même portée que la page courante (avec ou sans année). */
export const aiProposalAdminPaths = (
  portalPrefix: string,
  workspaceId: string,
  year: string,
): AiProposalAdminPaths => {
  const scope = year ? `${workspaceId}/${year}` : workspaceId;
  return { list: `/${portalPrefix}/${scope}/${AI_PROPOSAL_DOMAIN}/${LIST_SEGMENT}` };
};

/**
 * Message d'erreur affichable : le `message` / `detail` renvoyé par le serveur
 * (409 déjà décidée, 400 note manquante…) quand il existe, sinon le texte
 * générique passé par l'appelant. Calculé HORS du JSX (règle i18n 4a : jamais de
 * `.message` rendu directement).
 */
export const aiProposalErrorToMessage = (error: unknown, fallback: string): string => {
  const payload = (error as { response?: { data?: unknown } } | null)?.response?.data;
  if (typeof payload === 'string' && payload.trim() !== '' && payload.length < 300) return payload;
  const body = payload as { message?: unknown; detail?: unknown } | null | undefined;
  if (typeof body?.detail === 'string' && body.detail.trim() !== '') return body.detail;
  if (typeof body?.message === 'string' && body.message.trim() !== '') return body.message;
  return fallback;
};

// ─── États, opérations, sources ──────────────────────────────────────────

/**
 * Couleurs d'ÉTAT (jamais la marque du locataire) : en attente = avertissement
 * (une action humaine est attendue), approuvée = information (exécution en
 * cours), exécutée = succès, échec = danger, refusée = neutre (décision humaine,
 * pas un incident).
 */
export const statusTone = (status: AiProposalStatus): BadgeTone => {
  switch (status) {
    case 'PENDING':
      return 'warning';
    case 'APPROVED':
      return 'info';
    case 'EXECUTED':
      return 'success';
    case 'FAILED':
      return 'danger';
    default:
      return 'neutral';
  }
};

export const statusLabelKey = (status: AiProposalStatus): string => {
  switch (status) {
    case 'APPROVED':
      return 'core.aiProposal.status.approved';
    case 'REJECTED':
      return 'core.aiProposal.status.rejected';
    case 'EXECUTED':
      return 'core.aiProposal.status.executed';
    case 'FAILED':
      return 'core.aiProposal.status.failed';
    default:
      return 'core.aiProposal.status.pending';
  }
};

/** Libellé d'une option du filtre : un statut, ou « tous ». */
export const statusFilterLabelKey = (value: AiProposalStatusFilter): string =>
  value === ALL_STATUSES ? 'core.aiProposal.filter.all' : statusLabelKey(value);

export const operationLabelKey = (operation: AiProposalOperation): string => {
  switch (operation) {
    case 'UPDATE':
      return 'core.aiProposal.operation.update';
    case 'ACTION':
      return 'core.aiProposal.operation.action';
    default:
      return 'core.aiProposal.operation.create';
  }
};

export const sourceLabelKey = (source: AiProposalSource): string =>
  source === 'COPILOT' ? 'core.aiProposal.source.copilot' : 'core.aiProposal.source.apiKey';

/** Filtre de l'écran -> paramètre `status` du serveur (absent = toutes). */
export const statusFilterToParam = (filter: AiProposalStatusFilter): AiProposalStatus | undefined =>
  filter === ALL_STATUSES ? undefined : filter;

/** Seule une proposition en attente se décide. */
export const isDecidable = (proposal: Pick<AiProposalDto, 'status'>): boolean =>
  proposal.status === 'PENDING';

/**
 * Source affichée : le nom de la clé déposante, ou `fallback` (libellé
 * « Clé d'API » / « Copilote ») quand la clé n'a pas de nom ou a disparu.
 */
export const proposalSourceName = (
  proposal: Pick<AiProposalDto, 'source' | 'apiKeyName'>,
  fallback: string,
): string => (proposal.source === 'API_KEY' && proposal.apiKeyName ? proposal.apiKeyName : fallback);

// ─── Charge utile ────────────────────────────────────────────────────────

export type PayloadEntry = { key: string; value: string };

/** Profondeur maximale d'aplatissement ; au-delà, la valeur est rendue en JSON compact. */
const MAX_DEPTH = 3;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const scalarText = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return '';
  }
};

const isScalar = (value: unknown): boolean =>
  value === null || ['string', 'number', 'boolean'].includes(typeof value);

/**
 * Charge utile -> paires « clé : valeur » lisibles, dans l'ordre du serveur.
 * Les objets imbriqués s'aplatissent en clés pointées (`params.status`), une
 * liste de scalaires se joint par des virgules, le reste tombe en JSON compact.
 * Une valeur absente (null) est rendue vide : l'écran affiche alors un tiret.
 */
export const payloadEntries = (payload: unknown): PayloadEntry[] => {
  if (payload === null || payload === undefined) return [];
  if (!isPlainObject(payload)) return [{ key: '', value: scalarText(payload) }];

  const entries: PayloadEntry[] = [];
  const walk = (node: Record<string, unknown>, prefix: string, depth: number) => {
    Object.entries(node).forEach(([name, value]) => {
      const key = prefix === '' ? name : `${prefix}.${name}`;
      if (isPlainObject(value) && Object.keys(value).length > 0 && depth < MAX_DEPTH) {
        walk(value, key, depth + 1);
      } else if (Array.isArray(value) && value.length > 0 && value.every(isScalar)) {
        entries.push({ key, value: value.map(scalarText).join(', ') });
      } else {
        entries.push({ key, value: scalarText(value) });
      }
    });
  };
  walk(payload, '', 1);
  return entries;
};

/**
 * Résumé d'une charge utile sur une ligne (grille, carte mobile) :
 * `clé=valeur, clé=valeur…`, borné en nombre de paires et en longueur.
 */
export const summarizePayload = (
  payload: unknown,
  maxEntries: number = SUMMARY_MAX_ENTRIES,
  maxLength: number = SUMMARY_MAX_LENGTH,
): string => {
  const entries = payloadEntries(payload);
  if (entries.length === 0) return '';
  const shown = entries
    .slice(0, maxEntries)
    .map((entry) => (entry.key === '' ? entry.value : `${entry.key}=${entry.value}`))
    .join(', ');
  const text = entries.length > maxEntries ? `${shown}…` : shown;
  return text.length > maxLength ? `${text.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…` : text;
};

// ─── Enregistrement concerné ─────────────────────────────────────────────

/**
 * Identifiant de l'enregistrement concerné : celui visé par une mise à jour ou
 * une action, sinon celui que la création exécutée a renvoyé (`result.id`).
 * Vide tant que rien n'existe (création en attente, refusée ou en échec).
 */
export const proposalRecordId = (proposal: Pick<AiProposalDto, 'recordId' | 'result'>): string => {
  if (proposal.recordId && proposal.recordId.trim() !== '') return proposal.recordId.trim();
  const result = proposal.result;
  if (isPlainObject(result)) {
    const id = result.id;
    if (typeof id === 'number' && Number.isFinite(id)) return String(id);
    if (typeof id === 'string' && id.trim() !== '') return id.trim();
  }
  return '';
};

/** Nom d'entité -> jeton comparable : sans casse ni séparateur (`org-unit` ≡ `orgunit`). */
const entityToken = (value: string | null | undefined): string =>
  (value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

const ITEM_SUFFIX = '/:id';

const flattenRoutes = (routes: ModuleRouteNode[], acc: ModuleRouteNode[] = []): ModuleRouteNode[] => {
  routes.forEach((route) => {
    acc.push(route);
    if (Array.isArray(route.children) && route.children.length > 0) {
      flattenRoutes(route.children, acc);
    }
  });
  return acc;
};

/**
 * Chemin type (`/portal/:workspaceId/:year/finance/budget/:id`) de la fiche d'une
 * entité, lu dans le manifeste des routes publiées ; null quand le catalogue n'en
 * publie aucune (entité sans écran : l'écran n'affiche alors aucun lien).
 */
export const findRecordRouteTemplate = (
  routes: ModuleRouteNode[] | null | undefined,
  entityCode: string,
): string | null => {
  const wanted = entityToken(entityCode);
  if (wanted === '' || !Array.isArray(routes)) return null;
  const match = flattenRoutes(routes).find((route) => {
    if (route.enabled === false || typeof route.path !== 'string') return false;
    const definition = parseRoutePathDefinition(route.path, route.portalType);
    return (
      definition.suffix === ITEM_SUFFIX &&
      definition.includeWorkspaceId &&
      entityToken(definition.entity) === wanted
    );
  });
  return match ? match.path : null;
};

/**
 * Chemin de la fiche : espace de travail, année et identifiant substitués dans le
 * chemin type. null si l'identifiant manque ou si un paramètre reste ouvert.
 */
export const buildRecordPath = (
  template: string | null | undefined,
  workspaceId: string,
  year: string,
  recordId: string,
): string | null => {
  if (!template || recordId.trim() === '' || workspaceId.trim() === '') return null;
  const path = template
    .replace(':workspaceId', encodeURIComponent(workspaceId))
    .replace(':year', encodeURIComponent(year))
    .replace(':id', encodeURIComponent(recordId.trim()));
  return path.includes(':') ? null : path;
};

// ─── Décision ────────────────────────────────────────────────────────────

/** Longueur maximale de la note de décision saisie (bornée côté écran). */
export const MAX_NOTE_LENGTH = 1000;

/** Un refus exige une note non vide (le serveur répond 400 sinon). */
export const isRejectNoteValid = (note: string): boolean => {
  const trimmed = note.trim();
  return trimmed !== '' && trimmed.length <= MAX_NOTE_LENGTH;
};
